import { Input, TableContainer, Thead, Th, useDisclosure } from '@chakra-ui/react';
import { HttpStatusCode } from "axios";
import { createColumnHelper } from '@tanstack/react-table';
import React, { useState, useEffect } from 'react';
import { useAlertContext } from "../../../../context/AlertProvider";
import { Link } from 'react-router-dom';
import { useApi } from '../../../../context/ApiProvider';
import { TenantSaldoListType,TenantSaldoDtoType } from '../../../../shared/type/tenantTransaction.type';
import CustomAlert from '../../../CustomAlert/CustomAlert';
import ChooseMonths from '../../../ChooseMonths/ChooseMonths';
import CustomChakraButton from '../../../CustomChakraButton/CustomChakraButton';
import { DataTable } from '../../../DataTable/DataTable';
import FileInput from "../../../FileInput/FileInput";

const defaultAlert = {
  isOpen: false,
  id: 0,
  name: ''
};

const TenantTransactionsTable = () => {

  function getPreviousWeekMonday(): string {
    const today: Date = new Date();
    const day: number = today.getDay();
    const daysSinceMonday: number = (day + 6) % 7;
    const totalDaysBack: number = daysSinceMonday + 7;
    const previousMonday: Date = new Date(today);
    previousMonday.setDate(today.getDate() - totalDaysBack);
    const yyyy: number = previousMonday.getFullYear();
    const mm: string = String(previousMonday.getMonth() + 1).padStart(2, '0');
    const dd: string = String(previousMonday.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  function getPreviousWeekSunday(): string {
    const today: Date = new Date();
    const previousSunday: Date =new Date(today.setDate(today.getDate()-today.getDay()));
    const yyyy: number = previousSunday.getFullYear();
    const mm: string = String(previousSunday.getMonth() + 1).padStart(2, '0');
    const dd: string = String(previousSunday.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  const columnHelper = createColumnHelper<TenantSaldoDtoType>();
  const { setShow, setAlertProperties } = useAlertContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { apiSuperAdminFinanceController } = useApi();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tenantTransactionsData, setTenantTransactionsData] = useState<TenantSaldoDtoType[] | []>([]);
  const today = new Date();
  const [fromDate, setFromDate] = useState<string>(getPreviousWeekMonday());
  const [toDate, setToDate] = useState<string>(getPreviousWeekSunday());
  const [targetMonth, setTargetMonth] = useState<{ year: number; month: number; day: number }>({
      year: today.getFullYear(),
      month: today.getMonth()+1,
      day: today.getDate()
    });

  const [file, setFile] = useState<{
    data: FileList | undefined
   }>({
    data: undefined,
   });
   const [ textResponse, setTextResponse ] = useState<{
     data: string | undefined
   }>({
     data: undefined,
   })
  const fetchTenantTransactions = async () => {
    try {
      setIsLoading(true);
      const result = await apiSuperAdminFinanceController('tenant-saldo-list').get('');
      setTenantTransactionsData(result.data.list);
      setIsLoading(false);
    } catch (error) {
      console.warn(error);
      setIsLoading(false);
    }
  };

  const handleImport = async () => {
      let formData = new FormData();

        if (!file.data || file.data.length === 0) {
          return;
        }

        if (file.data) {
          formData.append("file", file.data[0]);
        }

      try {
        const response = await apiSuperAdminFinanceController('import-payment-csv-file').post('',  formData);
        if(response.status !== HttpStatusCode.Ok){
          setAlertProperties({
            timeout: 9000,
            title: "Błąd",
            description: `Błąd importu pliku`,
            status: "error"
          });
          setShow(true);
        }else {
        setAlertProperties({
            timeout: 9000,
            title: "Udany import pliku",
            description: `Poprawnie wczytano plik csv`,
            status: "success"
          });
        }
        fetchTenantTransactions();
        setShow(true);
      } catch (error: any) {

      }
    }

  const downloadTransferList = async () => {
    try {
      const response = await apiSuperAdminFinanceController('export-payments-to-tenant').get('', {
        responseType: 'blob'
      });
      const fileName = response.headers['content-disposition']?.split('filename=')[1];
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading sample file:', error);
    }
  }

   const downloadTransferListForDates = async () => {
      try {
        const response = await apiSuperAdminFinanceController('export-payments-to-tenant-for-dates')
            .post('', {
                fromDate: fromDate,
                toDate: toDate,
                year: targetMonth.year,
                month: targetMonth.month
              },{
                responseType: 'blob'
              }
            );
        const fileName = response.headers['content-disposition']?.split('filename=')[1];
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${fileName}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error('Error downloading sample file:', error);
      }
    }

  const columns = [
    columnHelper.accessor('tenantName', {
      cell: (info) => {
        const tenantName = info.getValue();
        return (
          <div className="text-sm font-semibold">
            {tenantName}
          </div>
        );
      },
      header: 'Nazwa Oddziału'
    }),
    columnHelper.accessor('currentTotalSaldo', {
      cell: (info) => {
        const { currentTotalSaldo } = info.row.original;
        return (
          <div className="text-sm font-medium">
            {currentTotalSaldo}
          </div>
        );
      },
      header: 'Aktualne saldo oddziału'
    }),

    columnHelper.accessor('monthSaldoList', {
      cell: (info) => {
        const { monthSaldoList } = info.row.original;
        return <div>
          {monthSaldoList.map((item, index) => (
            <div
              key={`${item.year}-${item.month}-${index}`}
              className="flex flex-col justify-center items-start"
            >
              <p className="font-normal text-sm text-gray-600">
                {item.year}.{item.month} {item.amount ?? '0.00'} —{' '}
              </p>
            </div>
          ))}
        </div>
      },
      header: 'Salda'
    }),
    columnHelper.accessor('transferMode', {
      cell: (info) => {
        const transferMode = info.getValue();
        return <div className="text-sm font-medium">{transferMode}</div>;
      },
      header: 'Typ transferu'
    }),
  ];

  useEffect(() => {
    fetchTenantTransactions();
  }, []);

  return (
    <section className="ParentsTable p-spacing-xl w-full pb-28">
      <div className="flex items-center gap-spacing-md">
        <div className="flex items-center gap-spacing-md">
         <div className="grow flex flex-col gap-spacing-lg">
            <div className="flex flex-col gap-spacing-xs">
              <label className="text-sm font-medium text-grayLight-700">
                Wpłaty od
              </label>
              <div className="w-44">
                <Input
                  placeholder="Wpłaty od"
                  type="date"
                  lang="pl"
                  required
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col gap-spacing-xs">
              <label className="text-sm font-medium text-grayLight-700">
                Wpłaty do
              </label>
              <div className="w-44">
                <Input
                  placeholder="Wpłaty do"
                  type="date"
                  lang="pl"
                  required
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                  }}
                />
              </div>
            </div>
          </div>
          <ChooseMonths mode="month" currentDate={targetMonth} setDate={setTargetMonth} />
          <div className="grow flex flex-col gap-spacing-lg">
            <CustomChakraButton
              hierarchy="tertiaryColor"
              size="lg"
              iconPosition="onlyText"
              buttonProps={{
                type: 'button',
                onClick: downloadTransferListForDates
              }}>
              Pobierz listę przelewów
            </CustomChakraButton>
          </div>
        </div>
        <div className='flex gap-spacing-5xl'>
          <div className="w-96">
            <FileInput
              onFileUpload={(file) => {
                setFile((prev) => ({ ...prev, data: file }));
              }}
              description="CSV"
            />
            <p>{textResponse.data}</p>
          </div>
          <div>
            <CustomChakraButton
              hierarchy="secondaryColor"
              size="sm"
              iconPosition="onlyText"
              buttonProps={{
                onClick: () => {handleImport()},
                className: "grow",
                disabled: !file.data,
              }}
            >
              Importuj CSV przelewów
            </CustomChakraButton>
          </div>
        </div>
      </div>
      <TableContainer className="Branches__table lg:p-spacing-3xl bg-white rounded-lg">
        <DataTable
          data={tenantTransactionsData}
          columns={columns}
          isLoading={isLoading}
          extraThead={
            <Thead>
              <Th colSpan={columns.length}>
                <div className="w-full flex items-center justify-between">
                  <p className="text-base font-semibold">Lista transakcji</p>
                </div>
              </Th>
            </Thead>
          }
        />
      </TableContainer>

    </section>
  );
};

export default TenantTransactionsTable;
